import { Play } from 'lucide-react';
import { useState } from 'react';
import { useI18nContext } from '@/i18n/i18n-react';
import type {
  ArchiveEdition,
  I18nText,
  NewsArea,
} from '@/lib/whats-new/edition-types';
import { cn } from '@/lib/utils';

/** Filters of the archive, in order (NEWS-41). */
const AREAS: readonly NewsArea[] = [
  'home',
  'campaigns',
  'analytics',
  'app',
  'audiences',
  'team',
];

/** Written out per area so the unused-token guard sees each label. */
const AREA_LABEL: Readonly<Record<NewsArea, I18nText>> = {
  home: (LL) => LL.whatsNew.areas.home(),
  campaigns: (LL) => LL.whatsNew.areas.campaigns(),
  analytics: (LL) => LL.whatsNew.areas.analytics(),
  app: (LL) => LL.whatsNew.areas.app(),
  audiences: (LL) => LL.whatsNew.areas.audiences(),
  team: (LL) => LL.whatsNew.areas.team(),
};

type Filter = 'all' | NewsArea;

/** An archive block and the edition its "Ver" opens. */
export interface ArchiveBlock {
  editionId: string;
  block: ArchiveEdition;
}

export interface EditionArchiveProps {
  /** Every block of every edition, newest first. */
  blocks: readonly ArchiveBlock[];
  /** Opens `editionId` on `slideIndex` (NEWS-40). */
  onSee: (editionId: string, slideIndex: number) => void;
}

/**
 * "Edições" (NEWS-39…42): the total, the area filters and every edition of
 * the archive with its items (area tag, text, "Ver"). The filter lives here,
 * so changing it re-renders only the list, never the card or its torus.
 */
export function EditionArchive({ blocks, onSee }: EditionArchiveProps) {
  const { LL } = useI18nContext();
  const [filter, setFilter] = useState<Filter>('all');
  const total = blocks.reduce((sum, { block }) => sum + block.items.length, 0);
  const filters: Array<{ value: Filter; label: string }> = [
    { value: 'all', label: LL.whatsNew.page.all() },
    ...AREAS.map((area) => ({ value: area, label: AREA_LABEL[area](LL) })),
  ];

  return (
    <section data-testid="edition-archive" className="flex flex-col">
      <div className="mb-3.5 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="text-[22px] font-semibold tracking-[-0.02em] text-[#15191E]">
          {LL.whatsNew.page.editions()}
        </h2>
        <span className="text-[13px] text-muted-foreground">
          {LL.whatsNew.page.total({ count: total })}
        </span>
      </div>

      <div
        role="group"
        aria-label={LL.whatsNew.page.filterLabel()}
        className="mb-5 flex flex-wrap gap-1.5"
      >
        {filters.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            aria-pressed={filter === value}
            onClick={() => setFilter(value)}
            className={cn(
              'h-[30px] rounded-full border px-3 text-[12.5px] font-medium text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring max-md:min-h-touch',
              filter === value &&
                'border-transparent bg-[#15191E] text-white hover:text-white'
            )}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-3">
        {blocks.map(({ editionId, block }) => {
          const items = block.items.filter(
            (item) => filter === 'all' || item.area === filter
          );
          if (items.length === 0) return null;
          return (
            <article
              key={`${editionId}:${block.number}`}
              data-edition={block.number}
              className="grid grid-cols-[96px_minmax(0,1fr)] items-start gap-4 max-md:grid-cols-1 max-md:gap-1.5"
            >
              <div className="pt-4 max-md:flex max-md:items-baseline max-md:gap-2.5 max-md:pt-1.5">
                <span className="block text-[30px] font-medium leading-none tracking-[-0.04em] text-[#15191E] max-md:text-[22px]">
                  {block.number}
                </span>
                <span className="mt-1.5 block text-xs text-muted-foreground max-md:mt-0">
                  {block.dates(LL)}
                </span>
              </div>
              <div className="flex min-w-0 flex-col gap-2.5 rounded-lg border bg-card px-5 py-[18px] max-md:rounded-mobile-card max-md:px-4">
                <h3 className="text-[17px] font-semibold tracking-[-0.01em] text-[#15191E]">
                  {block.title(LL)}
                </h3>
                <ul className="flex flex-col gap-1.5 max-md:gap-3">
                  {items.map((item) => {
                    const text = item.text(LL);
                    return (
                      <li
                        key={text}
                        data-archive-item
                        data-area={item.area}
                        className="flex items-center gap-2.5 text-[13.5px] text-[#4B5361] max-md:flex-wrap max-md:gap-x-2.5 max-md:gap-y-0"
                      >
                        <span
                          data-testid="archive-tag"
                          className="shrink-0 rounded px-[7px] py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.06em] bg-secondary text-secondary-foreground"
                        >
                          {AREA_LABEL[item.area](LL)}
                        </span>
                        <span
                          data-testid="archive-text"
                          className="min-w-0 flex-1 max-md:order-3 max-md:basis-full"
                        >
                          {text}
                        </span>
                        <button
                          type="button"
                          aria-label={LL.whatsNew.page.seeLabel({ item: text })}
                          onClick={() => onSee(editionId, item.slideIndex)}
                          className="ml-auto inline-flex h-[26px] shrink-0 items-center gap-[5px] rounded-full px-[9px] text-xs font-semibold text-[#155DFC] outline-none transition-colors hover:bg-secondary focus-visible:ring-2 focus-visible:ring-ring max-md:min-h-touch max-md:-mr-[9px]"
                        >
                          <Play
                            aria-hidden="true"
                            className="size-[11px] fill-current"
                            strokeWidth={0}
                          />
                          {LL.whatsNew.page.see()}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
